// 담당: 현정
// ⓘ 아이콘 + hover/focus 툴팁 — LCMD 결(Info 14px·fg-3, 다크 잉크 배경 흰 글씨, radius 0).
//   PerformanceSection 안에 있던 로컬 컴포넌트를 공용으로 추출(F-1). 호출 계약은 하위호환:
//   `text` 1줄은 그대로 동작하고, 구조체 본문이 필요하면 `children`(TipTitle/TipRow)을 넘긴다.
//
// 접근성: 트리거는 <button>(기존 tabIndex span은 role이 없었다). 툴팁은 CSS-only hover라
//   터치에서 열리지 않으므로 같은 내용을 sr-only 텍스트로도 반드시 제공한다(§5.6).
import type { ReactNode } from 'react'
import { Info } from 'lucide-react'

interface Props {
  /** 1줄 툴팁(하위호환). children이 있으면 본문 대신 sr-only 폴백으로만 쓰인다. */
  text?: string
  /** 구조체 본문 — TipTitle + TipRow 조합. */
  children?: ReactNode
  /** 스크린리더·터치 폴백 문구. 없으면 text. children을 쓸 땐 이 값을 반드시 채운다. */
  srText?: string
  /** 버튼 라벨. 없으면 중립 문구. */
  label?: string
}

export function InfoTip({ text, children, srText, label }: Props) {
  const sr = srText ?? text
  return (
    <button
      type="button"
      aria-label={label ?? '집계 기준 보기'}
      className="group relative inline-flex cursor-help border-0 bg-transparent p-0 align-middle text-fg-3 focus:outline-none focus-visible:shadow-focus"
    >
      <Info className="h-3.5 w-3.5 flex-none" aria-hidden />
      {sr && <span className="sr-only">{sr}</span>}
      <span
        role="tooltip"
        className={[
          'pointer-events-none absolute left-0 top-full z-20 mt-1.5 hidden break-keep bg-ink-800',
          'text-left font-text text-[11px] font-normal leading-relaxed text-white shadow-lf-2',
          'group-hover:block group-focus:block group-focus-visible:block',
          children ? 'w-[288px] p-[11px_13px]' : 'w-max max-w-[240px] px-2.5 py-1.5',
        ].join(' ')}
      >
        {children ?? text}
      </span>
    </button>
  )
}

/** 구조체 툴팁 제목 — 아래 행들과 hairline으로 분리. */
export function TipTitle({ children }: { children: ReactNode }) {
  return (
    <span className="block border-b border-white/[0.16] pb-[7px] font-text text-[12px] font-semibold leading-[1.4] text-white">
      {children}
    </span>
  )
}

/** 구조체 툴팁 행 — 라벨 고정폭 + 값(숫자는 tabular). */
export function TipRow({ k, v }: { k: string; v: ReactNode }) {
  return (
    <span className="flex gap-2 py-1 font-text text-[11px] leading-[1.5]">
      <span className="w-[52px] flex-none text-white/60">{k}</span>
      <span className="min-w-0 flex-1 tabular-nums text-white">{v}</span>
    </span>
  )
}
